import { useAuth } from "@clerk/nextjs";
import { Disclosure } from "@headlessui/react";
import { useMutation } from "@tanstack/react-query";
import classNames from "classnames";
import Modal from "components/Modal";
import useStudySnapshotFields from "components/StudySnapshot/hooks/useStudySnapshotFields";
import {
  FeedbackData,
  PaperDetails,
  StudyDetailsResponse,
  writeFeedback,
} from "helpers/api";
import useLabels from "hooks/useLabels";
import { useRouter } from "next/router";
import { useCallback, useReducer } from "react";
import { FeedbackItem } from "./FeedbackItem";
import {
  extractFeedbackDataFromState,
  FeedbackInputType,
  getJournalDetailsFeedbackItems,
  getProductDetailsFeedbackItems,
  getStudySnapshotFeedbackItems,
  StateType,
} from "./FeedbackUtil";

type PropsType = {
  open: boolean;
  onClose: () => void;
  paper: PaperDetails;
  studyDetails: StudyDetailsResponse | null;
  onSubmitSuccess: () => void;
};

const INITIAL_STATE = {};

// To spare the pain of creating 4 different fuctions to handle the input
// we use a reducer function

const reducer = (
  state: StateType,
  action: { type: string; field?: string; value?: FeedbackInputType }
) => {
  switch (action.type) {
    case "updateFieldValue":
      return { ...state, [action.field as string]: action.value };

    case "reset":
      return INITIAL_STATE;
    default:
      return INITIAL_STATE;
  }
};

const HumanFeedbackModal = ({
  open,
  onClose,
  paper,
  studyDetails,
  onSubmitSuccess,
}: PropsType) => {
  const [generalLabels, feedbackLabels] = useLabels(
    "general",
    "human-feedback-modal"
  );
  const router = useRouter();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { userId } = useAuth();

  const { mutate: writeFeedbackMutation } = useMutation(
    (payload: FeedbackData) => writeFeedback(payload)
  );

  const { fields } = useStudySnapshotFields({
    paperId: paper.id,
    badges: paper.badges,
    fetchOnMount: false,
  });

  const handleDismiss = () => {
    onClose();
    dispatch({ type: "reset" });
  };

  const onWriteFeedbackSuccess = () => {
    onSubmitSuccess();
    dispatch({ type: "reset" });
  };

  const handleSubmit = () => {
    const data = extractFeedbackDataFromState(
      state,
      router.query["q"] as string
    );

    const feedback: FeedbackData = {
      clerkUserId: userId as string,
      hashPaperId: paper.id,
      ...data,
    };

    writeFeedbackMutation(feedback, { onSuccess: onWriteFeedbackSuccess });
  };

  const handleFeedbackItemChange = useCallback(
    (id: string, values: FeedbackInputType) => {
      dispatch({ type: "updateFieldValue", field: id, value: values });
    },
    []
  );

  const isPristine = () => {
    return Object.values(state).every(
      (item) => (item as FeedbackInputType).pristine
    );
  };

  const productDetailsItems = getProductDetailsFeedbackItems(
    paper,
    router.query["extracted-answer"] as string
  );

  const journalDetailsItems = getJournalDetailsFeedbackItems(paper);
  const studySnapshotItems = getStudySnapshotFeedbackItems(
    paper,
    fields,
    studyDetails
  );

  return (
    <Modal
      open={open}
      title={feedbackLabels["title"]}
      subtitle={feedbackLabels["subtitle"]}
      buttonText={generalLabels["submit-btn-text"]}
      onButtonClicked={handleSubmit}
      buttonDisabled={isPristine()}
      onClose={handleDismiss}
      contentClassName="m-0"
    >
      <div>
        {productDetailsItems.length && (
          <Disclosure as="div" className="border-b border-b-border-base p-4">
            {({ open }) => (
              <>
                <Disclosure.Button className="flex justify-between w-full items-center">
                  <span className="base-bold">
                    {feedbackLabels["feedback-product-details-type-title"]}
                  </span>
                  <i
                    className={classNames(
                      "icon-chevron-down text-xl text-fg-muted",
                      {
                        "rotate-180 transform": open,
                      }
                    )}
                  />
                </Disclosure.Button>
                <Disclosure.Panel className="flex flex-col gap-3 mt-4">
                  {productDetailsItems.map((item) => (
                    <FeedbackItem
                      key={item.name}
                      payload={item}
                      onChange={handleFeedbackItemChange}
                    />
                  ))}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        )}
        <Disclosure as="div" className="border-b border-b-border-base p-4">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex justify-between w-full items-center">
                <span className="base-bold">
                  {feedbackLabels["feedback-study-snapshot-type-title"]}
                </span>
                <i
                  className={classNames(
                    "icon-chevron-down text-xl text-fg-muted",
                    {
                      "rotate-180 transform": open,
                    }
                  )}
                />
              </Disclosure.Button>
              <Disclosure.Panel className="flex flex-col gap-3 mt-4">
                {studySnapshotItems.map((item) => (
                  <FeedbackItem
                    key={item.name}
                    payload={item}
                    onChange={handleFeedbackItemChange}
                  />
                ))}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
        <Disclosure as="div" className="p-4">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex justify-between w-full items-center">
                <span className="base-bold">
                  {feedbackLabels["feedback-journal-details-type-title"]}
                </span>
                <i
                  className={classNames(
                    "icon-chevron-down text-xl text-fg-muted",
                    {
                      "rotate-180 transform": open,
                    }
                  )}
                />
              </Disclosure.Button>
              <Disclosure.Panel className="flex flex-col gap-3 mt-4">
                {journalDetailsItems.map((item) => (
                  <FeedbackItem
                    key={item.name}
                    payload={item}
                    onChange={handleFeedbackItemChange}
                  />
                ))}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>
    </Modal>
  );
};

export { HumanFeedbackModal };
